import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, CardContent, Grid, IconButton, Typography, makeStyles } from '@material-ui/core';
import { connect } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import { CardSectionTitle } from '../molecules/CardSectionTitle';
import { LinkButton } from '../atoms/LinkButton';
import { Modal } from '../molecules/Modal';
import { ItemRecord } from '../molecules/ItemRecord';
import { ProjectMultiSelector } from '../molecules/ProjectAutocompleteSelector';
import { ListItems } from './ListItems';
import { lightThemeV2 } from '../../settings/themes/lightThemeV2';
import { palette } from '../../settings/themes/colors';
import { useGetGroupProjects, useUnassignProjectToGroup } from '../../queries/queryContactGroups';
import { AssignProjectToGroupButton } from '../molecules/AssignProjectToGroupButton';
import { getCurrentUser } from '../../utils/auth';

export const useProjectsSectionStyles = () =>
  makeStyles({
    listContainer: {
      flex: 1,
      maxHeight: '226px',
      overflowY: 'scroll'
    },
    deleteIcon: {
      marginLeft: lightThemeV2.spacing(2),
      padding: lightThemeV2.spacing(1),
      color: palette.cerulean
    }
  })();

const AssignProjectToGroupContentComponent = ({ group, currentUser }) => {
  const classes = useProjectsSectionStyles();
  const { data = [], refetch: refetchProjectGroups } = useGetGroupProjects({
    params: { only_main_attributes: true },
    groupId: group.id
  });
  const onSuccess = () => {
    refetchProjectGroups();
  };
  const { mutate: unassignProjectToGroup } = useUnassignProjectToGroup({ onSuccess });
  const { client_id: clientId } = currentUser;
  const { messages: intlMessages } = useIntl();
  const [openAssignProjectModal, setOpenAssignProjectModal] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const handleAssignToProject = () => {
    setOpenAssignProjectModal(!openAssignProjectModal);
  };

  const handleClose = () => {
    setOpenAssignProjectModal(false);
  };

  const handleOnChange = value => {
    setSelectedProjects(value);
  };

  const projectGroups = data.map(({ id, name, updatedAt }) => {
    const handleUnassignProject = () => {
      unassignProjectToGroup({
        clientId,
        groupId: group.id,
        projectIds: [id]
      });
    };

    return {
      event: <Link to={`/projects/${id}`}>{name}</Link>,
      createdAt: updatedAt,
      controls: (
        <IconButton aria-label="delete card" onClick={handleUnassignProject} className={classes.deleteIcon}>
          <CloseIcon />
        </IconButton>
      )
    };
  });

  const labelUI = (
    <Typography>
      <FormattedMessage id="selector.project.assignAProject" />
      <Typography component="span">
        <i>*</i>
      </Typography>
    </Typography>
  );

  const buttonsUI = (
    <Grid container spacing={2}>
      <Grid item>
        <AssignProjectToGroupButton
          projects={selectedProjects}
          group={group}
          disabled={selectedProjects.length === 0}
          onClose={handleClose}
          refetchProjectGroups={refetchProjectGroups}
        />
      </Grid>
      <Grid item>
        <Button variant="outlined" onClick={handleClose}>
          <FormattedMessage id="common.cancel" />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <CardContent>
      <CardSectionTitle
        title={intlMessages['beneficiaries.section.projectsSubtitle']}
        actions={<LinkButton onClick={handleAssignToProject}>{intlMessages['beneficiaries.linkProject']}</LinkButton>}
      />
      <ListItems
        dataSource={projectGroups}
        emptyMsg={intlMessages['beneficiaries.empty.projects']}
        component={ItemRecord}
        className={classes.listContainer}
      />
      {openAssignProjectModal && (
        <Modal
          open={openAssignProjectModal}
          onClose={handleClose}
          title={<FormattedMessage id="beneficiaries.associateProject" />}
          subtitle={
            <Typography>
              <FormattedMessage id="beneficiaries.form.mandatoryMessage" />
              <Typography component="span">
                <i>*</i> .
              </Typography>
            </Typography>
          }
        >
          <Grid container spacing={4} direction="column">
            <Grid item>
              <ProjectMultiSelector
                name={'project'}
                selectedProjects={selectedProjects || []}
                label={labelUI}
                onChange={c => handleOnChange(c)}
              />
            </Grid>
            <Grid item>{buttonsUI}</Grid>
          </Grid>
        </Modal>
      )}
    </CardContent>
  );
};

AssignProjectToGroupContentComponent.propTypes = {
  group: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired
};

const mapStateToProps = state => ({ currentUser: getCurrentUser(state) });

export const AssignProjectToGroupContent = connect(mapStateToProps)(AssignProjectToGroupContentComponent);
